import { useState, useEffect } from "react";
import { X, Share, Download } from "lucide-react";

const DISMISS_KEY = "fym_install_dismissed_at";
const DISMISS_DAYS = 14;

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isMobile() {
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent) || window.innerWidth < 768;
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function wasDismissedRecently() {
  const ts = localStorage.getItem(DISMISS_KEY);
  if (!ts) return false;
  return Date.now() - parseInt(ts) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

export default function InstallBanner() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  useEffect(() => {
    if (!isMobile() || isStandalone() || wasDismissedRecently()) return;

    if (isIOS()) {
      setIsIOSDevice(true);
      setShow(true);
      return;
    }

    // Android: listen for native install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  }

  async function handleInstall() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setShow(false);
    setDeferredPrompt(null);
  }

  if (!show) return null;

  return (
    <div className="fixed bottom-16 left-0 right-0 z-40 px-3 pb-2">
      <div
        className="flex items-center gap-3 rounded-2xl px-4 py-3 shadow-2xl border border-white/10"
        style={{ background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)" }}
      >
        {/* Icon */}
        <div className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ background: "#BFFF00" }}>
          <img src="/LOGO_FYM-01.svg" alt="FYM" className="w-full h-full object-contain p-1" />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-semibold leading-tight">ติดตั้งเป็นแอพ</p>
          {isIOSDevice ? (
            <p className="text-gray-400 text-xs mt-0.5 leading-snug">
              กด <Share size={11} className="inline mb-0.5" /> Share แล้วเลือก{" "}
              <span className="text-[#BFFF00] font-medium">Add to Home Screen</span>
            </p>
          ) : (
            <p className="text-gray-400 text-xs mt-0.5">เปิดเร็วขึ้น · ใช้ได้แบบ offline</p>
          )}
        </div>

        {/* Action */}
        {!isIOSDevice && (
          <button
            onClick={handleInstall}
            className="flex-shrink-0 flex items-center gap-1.5 bg-[#BFFF00] text-black text-xs font-bold px-3 py-1.5 rounded-xl active:scale-95 transition-transform"
          >
            <Download size={13} />
            ติดตั้ง
          </button>
        )}

        {/* Dismiss */}
        <button
          onClick={dismiss}
          className="flex-shrink-0 p-1 text-gray-500 hover:text-gray-300"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
